import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';

const AppLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);

  const tiles = [
    {
      title: 'Kosztorysy',
      path: '/app/estimates',
      iconSrc: '/assets/icons/request_quote_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'EasyEstimate',
      path: '/app/easy-estimate',
      iconSrc: '/assets/icons/price_check.svg',
    },
    {
      title: 'Notatki',
      path: '/app/notes',
      iconSrc: '/assets/icons/notes_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Karty materiałowe',
      path: '/app/materials_approvals',
      iconSrc: '/assets/icons/description_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Harmonogamy',
      path: '/app/schedules',
      iconSrc: '/assets/icons/schedule_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Logistyka',
      path: '/app/maps',
      iconSrc: '/assets/icons/map_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'HR',
      path: '/app/hr',
      iconSrc: '/assets/icons/diversity_3_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Kadry',
      path: '/app/kadry',
      iconSrc: '/assets/icons/person.svg',
    },
    // Druga linia (bez przekierowań na razie)
    {
      title: 'Umowy',
      path: '/app/contracts',
      iconSrc: '/assets/icons/contract_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Wyposażenie',
      path: '/app/equipment',
      iconSrc: '/assets/icons/construction_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Tasker',
      path: '/app/tasker',
      iconSrc: '/assets/icons/tasker.svg',
    },
    {
      title: 'Magazyn',
      iconSrc: '/assets/icons/warehouse_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Requester',
      path: '/app/requester',
      iconSrc: '/assets/icons/hand_gesture_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
    {
      title: 'Generator QR',
      path: '/app/qr-generator',
      iconSrc: '/assets/icons/qr_code_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg',
    },
  ];

  // Pojedynczy kafelek aplikacji z obsługą fallbacku ikony
  const AppTile: React.FC<{ title: string; path?: string; iconSrc: string }> = ({ title, path, iconSrc }) => {
    const isClickable = Boolean(path);
    const [iconOk, setIconOk] = React.useState(true);
    const displaySrc = iconOk ? iconSrc : '/assets/icons/folder.svg';
    return (
      <Box
        onClick={() => { if (isClickable) navigate(path as string); }}
        role={isClickable ? 'button' : 'group'}
        tabIndex={isClickable ? 0 : -1}
        onKeyDown={(e) => { if (isClickable && (e.key === 'Enter' || e.key === ' ')) navigate(path as string); }}
        sx={{
          bgcolor: '#fff',
          border: '1px solid #EDEDED',
          borderRadius: '4px',
          cursor: isClickable ? 'pointer' : 'default',
          aspectRatio: '1 / 1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: '12px',
          transition: 'background-color .15s ease, transform .02s ease',
          userSelect: 'none',
          '&:hover': { backgroundColor: '#F5F5FF' },
          '&:active': isClickable ? { transform: 'translateY(1px)' } : undefined,
          '&:focus-visible': isClickable ? { outline: '2px solid #4040DB', outlineOffset: 2 } : undefined
        }}
      >
        {/* Preload do wykrycia braku pliku */}
        <Box component="img" src={iconSrc} alt="" sx={{ display: 'none' }} onError={() => setIconOk(false)} />
        <Box
          sx={{
            width: 48,
            height: 48,
            mb: 1.5,
            bgcolor: '#3F3FDC',
            WebkitMaskImage: `url(${displaySrc})`,
            maskImage: `url(${displaySrc})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain'
          }}
          aria-hidden
        />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            color: '#020242',
            fontSize: '14px',
            fontWeight: 500,
            textAlign: 'center',
            lineHeight: '18px'
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek w stylu strony Projekty + ta sama pozycja (desktop/mobile) */}
      <Box sx={isMobile ? {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: '16.5px',
        mb: 2
      } : {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mt: 0,
        mb: 2
      }}>
        <Typography
          component="h1"
          sx={isMobile ? {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0
          } : {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Aplikacje
        </Typography>
      </Box>
      <Box
        sx={{
          mt: '28px',
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(6, 1fr)',
            xl: 'repeat(6, 1fr)'
          }
        }}
      >
        {tiles.map(({ title, path, iconSrc }) => (
          <AppTile key={title} title={title} path={path} iconSrc={iconSrc} />
        ))}
      </Box>
    </Box>
  );
};

export default AppLandingPage;


